/** The staff percentage: a console-only discount on every count for the
 *  staff the cameras cannot recognise. At 10% every footfall figure is shown
 *  as 90% of what the API returned — entries, exits, the demographics and
 *  their staff twins alike, so shares and conversion move with it. Nothing
 *  is written back; the API and the database never see it. */
import type { HourOfDayPoint, MetricTotals } from "../types";

export const MIN_STAFF_PERCENT = 0;
export const MAX_STAFF_PERCENT = 50;
/** Where the slider starts on every visit — it is never remembered. */
export const DEFAULT_STAFF_PERCENT = 10;

const METRICS = [
  "entries",
  "exits",
  "male",
  "female",
  "age_0_17",
  "age_18_29",
  "age_30_44",
  "age_45_plus",
  "unknown_demo",
  "staff_entries",
  "staff_exits",
  "staff_male",
  "staff_female",
  "staff_age_0_17",
  "staff_age_18_29",
  "staff_age_30_44",
  "staff_age_45_plus",
  "staff_unknown_demo",
] as const;

/** Clamp a percentage into the slider's range; anything unreadable is 0. */
export function clampStaffPercent(value: number | null | undefined): number {
  if (value === null || value === undefined || !Number.isFinite(value)) return MIN_STAFF_PERCENT;
  return Math.min(MAX_STAFF_PERCENT, Math.max(MIN_STAFF_PERCENT, Math.round(value)));
}

/** Every count in the row with `staffPercent` percent taken off, rounded. */
export function scaleMetrics<T extends MetricTotals>(row: T, staffPercent: number): T {
  if (staffPercent <= 0) return row;
  const factor = 1 - staffPercent / 100;
  const out = { ...row };
  for (const metric of METRICS) {
    out[metric] = Math.round(row[metric] * factor) as T[typeof metric];
  }
  return out;
}

export function scaleRows<T extends MetricTotals>(rows: T[], staffPercent: number): T[] {
  return staffPercent <= 0 ? rows : rows.map((row) => scaleMetrics(row, staffPercent));
}

/** The peak-hours series with `staffPercent` percent taken off. */
export function scaleHours<T extends HourOfDayPoint>(rows: T[], staffPercent: number): T[] {
  if (staffPercent <= 0) return rows;
  const factor = 1 - staffPercent / 100;
  return rows.map((row) => ({
    ...row,
    entries: Math.round(row.entries * factor),
    avg_entries: row.avg_entries * factor,
    staff_entries: Math.round(row.staff_entries * factor),
    avg_staff_entries: row.avg_staff_entries * factor,
  }));
}
